import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { CourseDetailsService } from '../services/llmClient/CourseDetailsService';
import {
  fetchCourseByIdWithUnits,
  saveCourseWithUnits,
  updateCoursePosition,
} from '../services/supabaseClient/courseService';
import eventStreamService from '../services/supabaseClient/eventStreamService';
import { addCourse } from './coursesSlice';

// 🧠 Load initial state from localStorage
const loadCourseFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('currentCourseWithUnits');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        course: parsed.course || null,
        units: parsed.units || [],
      };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('⚠️ Failed to parse course from localStorage:', e);
  }

  return {
    course: null,
    units: [],
  };
};

// ⏳ Generate a course via LLM and save to Supabase
export const generateCourseContentThunk = createAsyncThunk(
  'courseDetails/generateCourseContent',
  async ({ courseData, profileData }, thunkAPI) => {
    try {
      void eventStreamService.emitEvent({
        userId: profileData?.id || null,
        sessionId: `course_creation:${Date.now()}`,
        streamKey: `course_creation:${courseData?.title || 'untitled'}`,
        eventFamily: 'course_creation',
        eventType: 'course_creation_requested',
        eventName: 'course_creation_requested',
        origin: 'course_details',
        actorType: 'user',
        actorId: profileData?.id || null,
        entityType: 'course_request',
        entityId: courseData?.title || 'untitled',
        payload: {
          title: courseData?.title || '',
          lessons: courseData?.lessons || null,
          difficultyLevel: courseData?.difficultyLevel || null,
        },
      });

      const course = await CourseDetailsService.generateCourse(courseData, profileData);
      const parsedCourse = typeof course === 'string' ? JSON.parse(course) : course;

      const { data: savedCourse, error } = await saveCourseWithUnits(parsedCourse);

      if (error) {
        const detailedError =
          error?.message ||
          error?.details ||
          error?.hint ||
          (typeof error === 'string' ? error : null) ||
          'Failed to save course to database';
        return thunkAPI.rejectWithValue(detailedError);
      }

      // Refresh the persisted course to guarantee dashboard/modal sees DB-normalized units.
      const { data: refreshedCourse } = await fetchCourseByIdWithUnits(savedCourse.id);
      const courseWithUnits = {
        ...(refreshedCourse || savedCourse),
        units: (refreshedCourse || savedCourse).units || [],
      };

      thunkAPI.dispatch(addCourse(courseWithUnits));
      localStorage.setItem('lastGeneratedCourse', JSON.stringify(courseWithUnits));
      void eventStreamService.emitEvent({
        userId: profileData?.id || null,
        sessionId: `course_creation:${courseWithUnits.id}`,
        streamKey: `course_creation:${courseWithUnits.id}`,
        eventFamily: 'course_creation',
        eventType: 'course_creation_succeeded',
        eventName: 'course_creation_succeeded',
        origin: 'course_details',
        actorType: 'system',
        actorId: 'course_creation_service',
        entityType: 'course',
        entityId: courseWithUnits.id,
        courseId: courseWithUnits.id,
        success: true,
        payload: {
          title: courseWithUnits.title,
          unitCount: Array.isArray(courseWithUnits.units) ? courseWithUnits.units.length : 0,
        },
      });

      return courseWithUnits;
    } catch (err) {
      void eventStreamService.emitEvent({
        userId: profileData?.id || null,
        sessionId: `course_creation:${Date.now()}`,
        streamKey: `course_creation:${courseData?.title || 'untitled'}`,
        eventFamily: 'course_creation',
        eventType: 'course_creation_failed',
        eventName: 'course_creation_failed',
        origin: 'course_details',
        actorType: 'system',
        actorId: 'course_creation_service',
        entityType: 'course_request',
        entityId: courseData?.title || 'untitled',
        success: false,
        errorCode: err?.message || 'course_creation_failed',
        payload: {
          title: courseData?.title || '',
        },
      });
      return thunkAPI.rejectWithValue(err.message || 'Course generation failed');
    }
  }
);

export const generateCourseFromPromptThunk = createAsyncThunk(
  'courseDetails/generateCourseFromPrompt',
  async ({ prompt, profileData }, thunkAPI) => {
    try {
      void eventStreamService.emitEvent({
        userId: profileData?.id || null,
        sessionId: `course_creation_prompt:${Date.now()}`,
        streamKey: `course_creation_prompt:${String(prompt || '').slice(0, 80)}`,
        eventFamily: 'course_creation',
        eventType: 'course_creation_prompt_requested',
        eventName: 'course_creation_prompt_requested',
        origin: 'course_details',
        actorType: 'user',
        actorId: profileData?.id || null,
        entityType: 'course_prompt',
        entityId: String(prompt || '').slice(0, 80) || 'untitled_prompt',
        payload: {
          promptLength: String(prompt || '').trim().length,
        },
      });

      const courseWithUnits = await CourseDetailsService.generateCourseFromPrompt(
        prompt,
        profileData
      );
      const { data: savedCourse, error } = await saveCourseWithUnits(courseWithUnits);

      if (error) {
        const detailedError =
          error?.message ||
          error?.details ||
          error?.hint ||
          (typeof error === 'string' ? error : null) ||
          'Failed to save course to database';
        return thunkAPI.rejectWithValue(detailedError);
      }

      const { data: refreshedCourse } = await fetchCourseByIdWithUnits(savedCourse.id);
      const normalizedCourse = {
        ...(refreshedCourse || savedCourse),
        units: (refreshedCourse || savedCourse).units || [],
      };

      thunkAPI.dispatch(addCourse(normalizedCourse));
      localStorage.setItem('lastGeneratedCourse', JSON.stringify(normalizedCourse));
      void eventStreamService.emitEvent({
        userId: profileData?.id || null,
        sessionId: `course_creation:${normalizedCourse.id}`,
        streamKey: `course_creation:${normalizedCourse.id}`,
        eventFamily: 'course_creation',
        eventType: 'course_creation_prompt_succeeded',
        eventName: 'course_creation_prompt_succeeded',
        origin: 'course_details',
        actorType: 'system',
        actorId: 'course_creation_service',
        entityType: 'course',
        entityId: normalizedCourse.id,
        courseId: normalizedCourse.id,
        success: true,
        payload: {
          title: normalizedCourse.title,
          unitCount: Array.isArray(normalizedCourse.units) ? normalizedCourse.units.length : 0,
        },
      });

      return normalizedCourse;
    } catch (err) {
      void eventStreamService.emitEvent({
        userId: profileData?.id || null,
        sessionId: `course_creation_prompt:${Date.now()}`,
        streamKey: `course_creation_prompt:${String(prompt || '').slice(0, 80)}`,
        eventFamily: 'course_creation',
        eventType: 'course_creation_prompt_failed',
        eventName: 'course_creation_prompt_failed',
        origin: 'course_details',
        actorType: 'system',
        actorId: 'course_creation_service',
        entityType: 'course_prompt',
        entityId: String(prompt || '').slice(0, 80) || 'untitled_prompt',
        success: false,
        errorCode: err?.message || 'course_creation_prompt_failed',
        payload: {
          promptLength: String(prompt || '').trim().length,
        },
      });
      return thunkAPI.rejectWithValue(err.message || 'Course generation failed');
    }
  }
);

// 🌐 Update current lesson index in DB and Redux
export const syncLessonIndexThunk = createAsyncThunk(
  'courseDetails/syncLessonIndex',
  async ({ courseId, index }, thunkAPI) => {
    try {
      await updateCoursePosition(courseId, index);
      thunkAPI.dispatch(updateCurrentLessonIndex(index)); // update local state
      return index;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('❌ Lesson index sync error:', err.message);
      return thunkAPI.rejectWithValue('Failed to update lesson index');
    }
  }
);

// 🧠 Initial state
const initialState = {
  ...loadCourseFromLocalStorage(),
  loading: false,
  error: null,
};

// 🧩 Slice
const courseDetailsSlice = createSlice({
  name: 'courseDetails',
  initialState,
  reducers: {
    clearCourse(state) {
      state.course = null;
      state.units = [];
      state.loading = false;
      state.error = null;
      localStorage.removeItem('currentCourseWithUnits');
    },
    updateCurrentLessonIndex(state, action) {
      const index = action.payload;
      if (state.course) {
        state.course.currentLessonIndex = index;

        // Persist update to localStorage
        localStorage.setItem(
          'currentCourseWithUnits',
          JSON.stringify({
            course: state.course,
            units: state.units,
          })
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateCourseContentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateCourseContentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.course = action.payload;
        state.units = action.payload.units || [];
        localStorage.setItem(
          'currentCourseWithUnits',
          JSON.stringify({
            course: state.course,
            units: state.units,
          })
        );
      })
      .addCase(generateCourseContentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateCourseFromPromptThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateCourseFromPromptThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.course = action.payload;
        state.units = action.payload.units || [];
        localStorage.setItem(
          'currentCourseWithUnits',
          JSON.stringify({
            course: state.course,
            units: state.units,
          })
        );
      })
      .addCase(generateCourseFromPromptThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(syncLessonIndexThunk.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

// ✅ Actions
export const { clearCourse, updateCurrentLessonIndex } = courseDetailsSlice.actions;

// ✅ Reducer
export default courseDetailsSlice.reducer;

// ✅ Selectors
export const selectGeneratedCourse = (state) => state.courseDetails.course;
export const selectGeneratedUnits = (state) => state.courseDetails.units;
export const selectCourseLoading = (state) => state.courseDetails.loading;
export const selectCourseError = (state) => state.courseDetails.error;
